import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import Layout from 'components/common/layout/Layout';
import FileList from 'components/common/lists/FileList';
import SingleBtn from 'components/common/elements/SingleBtn';
import LoadingLayout from 'components/common/layout/LoadingLayout';
import styles from 'styles/pages/common/NoticeDetail.module.scss';
import { ROUTE_PATH } from 'constants/routePath';
import { NoticeInfoDefaultValues } from 'constants/notice';
import { getNotice, deleteNotice } from 'apis/notice';
import useModal from 'hooks/useModal';
import useGoBack from 'hooks/useGoBack';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'store/auth';

const NoticeDetail = () => {
  const userInfo = useAtomValue(userInfoAtom);
  const { openModal } = useModal();
  const goToList = useGoBack();
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(Boolean);
  const id = Number(pathnames[pathnames.length - 1]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [noticeData, setNoticeData] = useState(NoticeInfoDefaultValues);

  const { data: getNoticeData } = useQuery(
    ['getNoticeInfo', id],
    () => getNotice(id),
    {
      onError: () =>
        openModal({
          title: '공지사항 조회 실패',
          text: '다시 시도해 주세요.',
          onSubmit: () => goToList(),
          onClickOutside: () => goToList(),
        }),
      enabled: !!id,
    }
  );

  useEffect(() => {
    setNoticeData(getNoticeData?.data || NoticeInfoDefaultValues);
    window.scrollTo(0, 0);
  }, [getNoticeData]);

  const handleDelete = () =>
    openModal({
      isConfirm: true,
      title: '공지사항 삭제',
      text: '등록하신 공지사항을 삭제하시겠습니까?',
      onSubmit: () => {
        setIsLoading(true);
        deleteNoticeMutation();
      },
    });

  const { mutate: deleteNoticeMutation } = useMutation(() => deleteNotice(id), {
    onSuccess: () => goToList(),
    onError: () => {
      setIsLoading(false);
      openModal({
        title: '공지사항 삭제 실패',
        text: '카이아이 고객센터로 문의해 주세요.',
      });
    },
  });

  return (
    <>
      {isLoading ? (
        <LoadingLayout />
      ) : (
        <Layout>
          <div className={styles.title_wrapper}>
            <h1 className="main_title">공지사항</h1>
            <SingleBtn
              btnText="목록"
              hasOutline={true}
              btnStyle={{
                height: '41px',
                fontSize: '14px',
              }}
              onClick={() => goToList()}
            />
          </div>
          <div className={styles.notice_title}>
            <p className={styles.title}>{noticeData?.title || ''}</p>
            <span className="fs14 px24">{noticeData?.createdAt || ''}</span>
          </div>
          <textarea
            name="notice"
            id="notice"
            rows={10}
            className={styles.textarea}
            value={noticeData?.content || ''}
            readOnly
          />
          {noticeData?.noticeFileList &&
            noticeData.noticeFileList.length > 0 && (
              <FileList value={noticeData?.noticeFileList || []} />
            )}
          <div className="flex gap16 justify_end mt32 mb40">
            {noticeData?.writerId === userInfo?.userId &&
            noticeData?.writerRole === userInfo?.userRole ? (
              <>
                <SingleBtn
                  btnText="삭제"
                  btnStyle={{
                    width: '97px',
                    fontSize: '14px',
                  }}
                  onClick={handleDelete}
                />
                <SingleBtn
                  btnText="수정"
                  isBtnActive={true}
                  btnStyle={{
                    width: '97px',
                    fontSize: '14px',
                  }}
                  onClick={() =>
                    navigate(`${ROUTE_PATH.common.NOTICE_REGISTER}?id=${id}`)
                  }
                />
              </>
            ) : (
              <>
                {noticeData?.prevNoticeId && (
                  <SingleBtn
                    btnText="이전"
                    btnStyle={{
                      width: '97px',
                      fontSize: '14px',
                    }}
                    onClick={() =>
                      navigate(
                        `${ROUTE_PATH.common.NOTICE}/${noticeData?.prevNoticeId}`
                      )
                    }
                  />
                )}
                {noticeData?.nextNoticeId && (
                  <SingleBtn
                    btnText="다음"
                    isBtnActive={true}
                    btnStyle={{
                      width: '97px',
                      fontSize: '14px',
                    }}
                    onClick={() =>
                      navigate(
                        `${ROUTE_PATH.common.NOTICE}/${noticeData?.nextNoticeId}`
                      )
                    }
                  />
                )}
              </>
            )}
          </div>
        </Layout>
      )}
    </>
  );
};

export default NoticeDetail;
